import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  AlertTriangle,
  Calendar,
  Eye,
  RotateCcw,
  Trash2
} from 'lucide-react';
import serviceService from '../services/serviceService';
import serviceCategoryService from '../services/serviceCategoryService';
import CustomSelect from '../components/common/CustomSelect';
import Modal from '../components/common/Modal';

const ServiceTrashPage = () => {
  const navigate = useNavigate();
  
  const [deletedServices, setDeletedServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    paymentType: ''
  });

  const [viewingService, setViewingService] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);

  const loadCategories = useCallback(async () => {
    try {
      const data = await serviceCategoryService.getActiveCategories();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error cargando categorías:', error);
    }
  }, []);

  const loadDeletedServices = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await serviceService.getDeletedServices({
        page,
        limit: itemsPerPage,
        search: searchTerm,
        category: filters.category,
        paymentType: filters.paymentType
      });
      setDeletedServices(response.services || []);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalItems(response.pagination?.totalItems || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error cargando servicios eliminados:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, itemsPerPage]);

  const handleRestoreService = async (serviceId, serviceName) => {
    const confirmed = window.confirm(
      `¿Estás seguro de que quieres restaurar el servicio "${serviceName}"?\n\nVolverá a estar activo y visible en la lista principal.`
    );
    
    if (confirmed) {
      try {
        await serviceService.restoreService(serviceId);
        loadDeletedServices();
      } catch (error) {
        console.error('Error restaurando servicio:', error);
        alert('Error al restaurar el servicio. Por favor, inténtalo de nuevo.');
      }
    }
  };

  const confirmPermanentDelete = (service) => {
    setServiceToDelete(service);
    setShowDeleteModal(true);
  };

  const executePermanentDelete = async () => {
    if (!serviceToDelete) return;
    try {
      await serviceService.permanentDeleteService(serviceToDelete._id);
      setShowDeleteModal(false);
      setServiceToDelete(null);
      loadDeletedServices();
    } catch (error) {
      console.error('Error eliminando servicio permanentemente:', error);
      alert('Error al eliminar el servicio de forma permanente.');
    }
  };

  const handleViewService = (service) => {
    setViewingService(service);
    setShowViewModal(true);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadDeletedServices(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    loadDeletedServices(1);
  };

  useEffect(() => {
    loadCategories();
    loadDeletedServices(1);
  }, [loadDeletedServices, loadCategories]);

  useEffect(() => {
    if (currentPage === 1) {
      loadDeletedServices(1);
    } else {
      setCurrentPage(1);
      loadDeletedServices(1);
    }
  }, [searchTerm, filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ category: '', paymentType: '' });
  };

  // Ya vienen filtrados desde el backend
  const filteredServices = deletedServices; 

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button className="btn btn-secondary btn-icon" onClick={() => navigate('/services')}>
          <ArrowLeft size={20} />
          Volver a Servicios
        </button>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Papelera de Servicios</h1>
        <p className="text-gray-600">
          Aquí puedes ver, restaurar o eliminar definitivamente servicios descartados.
        </p>
      </div>

      {/* Contenedor de filtros */}
      <div className="card mb-6 filters-container">
        <div className="card-content">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-64">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nombre o descripción..."
                className="form-input"
              />
            </div>
            <div className="w-48">
              <CustomSelect
                label="Categoría"
                value={filters.category}
                onChange={(value) => handleFilterChange('category', value)}
                options={[
                  { value: '', label: 'Todas las categorías' },
                  ...categories.map(cat => ({ value: cat._id, label: cat.name }))
                ]}
                placeholder="Todas las categorías"
              />
            </div>
            <div className="w-48">
              <CustomSelect
                label="Tipo de Pago"
                value={filters.paymentType}
                onChange={(value) => handleFilterChange('paymentType', value)}
                options={[
                  { value: '', label: 'Todos los tipos' },
                  { value: 'ONE_TIME', label: 'Pago Único' },
                  { value: 'RECURRING', label: 'Pago Recurrente' }
                ]}
                placeholder="Todos los tipos"
              />
            </div>
            <div className="w-48">
              <label className="form-label">&nbsp;</label>
              <button onClick={clearFilters} className="btn btn-outline w-full">
                🗑️ Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="records-per-page">
        <label htmlFor="itemsPerPage">Registros por página:</label>
        <select
          id="itemsPerPage"
          value={itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>
      </div>

      <div className="table-container">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="loading-spinner"></div>
            <p className="mt-4 text-gray-600">Cargando papelera...</p>
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">🗑️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Papelera vacía</h3>
            <p className="text-gray-600 mb-6">No hay servicios en la papelera</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Categoría</th>
                <th>Precio</th>
                <th>Eliminado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredServices.map(service => (
                <tr key={service._id}>
                  <td>
                    <div className="font-medium text-gray-900">{service.name}</div>
                    <div className="text-sm text-gray-500" style={{ 
                        maxWidth: '250px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word'
                      }}>
                      {service.description || 'Sin descripción'}
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-primary">{service.category?.name}</span>
                  </td>
                  <td className="text-sm text-gray-700">${service.price.toLocaleString()}</td>
                  <td className="text-sm text-gray-700">
                    <div className="flex items-center">
                      <Calendar size={14} className="mr-1" />
                      {service.deletedAt ? new Date(service.deletedAt).toLocaleDateString() : 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewService(service)}
                        className="btn btn-primary btn-sm"
                        title="Ver detalles"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleRestoreService(service._id, service.name)}
                        className="btn btn-success btn-sm"
                        title="Restaurar servicio"
                      >
                        <RotateCcw size={16} />
                      </button>
                      <button
                        onClick={() => confirmPermanentDelete(service)}
                        className="btn btn-danger btn-sm"
                        title="Eliminar permanentemente"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>
              Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems}
            </span>
          </div>
          <div className="pagination-controls">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="btn btn-outline btn-sm">← Anterior</button>
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;

                return (
                  <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-outline'}`}>
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="btn btn-outline btn-sm">Siguiente →</button>
          </div>
        </div>
      )}

      {/* Modal de visualización */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Detalles del Servicio Eliminado"
        onSubmit={(e) => { e.preventDefault(); setShowViewModal(false); }}
        submitText="Cerrar"
        hideCancel={true}
      >
        {viewingService && (
          <div className="space-y-4 text-left">
            <div>
              <h4 className="text-sm font-semibold text-gray-500">Nombre</h4>
              <p className="text-base text-gray-900 font-medium">{viewingService.name}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-500">Categoría</h4>
              <p className="text-base text-gray-900 mt-1">
                <span className="badge badge-primary">{viewingService.category?.name}</span>
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-500">Descripción</h4>
              <p className="text-base text-gray-900 mt-1 p-3 bg-gray-50 rounded-md border">{viewingService.description || 'Sin descripción'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-500">Precio</h4>
                <p className="text-base font-medium text-gray-900">${viewingService.price.toLocaleString()}</p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-500">Tipo de Pago</h4>
                <p className="text-base text-gray-900">
                  {viewingService.paymentType === 'ONE_TIME' ? 'Pago Único' : `Recurrente (${viewingService.billingPeriodText || 'N/A'})`}
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de confirmación de eliminación permanente */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="¡Advertencia de Eliminación Permanente!"
        onSubmit={(e) => { e.preventDefault(); executePermanentDelete(); }}
        submitText="Eliminar Permanentemente"
        cancelText="Cancelar"
        submitButtonClass="btn-danger"
      >
        <div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
          <p className="font-medium text-lg mb-2">¿Estás a punto de borrar definitivamente este servicio?</p>
          <p>Se eliminará el servicio: <strong>{serviceToDelete?.name}</strong>.</p>
          <p className="mt-2 text-sm font-bold">Esta acción purgará los datos de la base de datos y NO se puede deshacer.</p>
        </div>
      </Modal>

      <div style={{ marginTop: '48px' }}>
        <div style={{ backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <AlertTriangle size={16} style={{ color: '#d97706', marginTop: '2px', flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: '14px', color: '#92400e', margin: 0 }}>
                <strong>Importante:</strong> Los servicios en la papelera mantienen su información resguardada, pero si usas el botón rojo, se purgarán sin recuperación posible.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceTrashPage;
